import { useCallback, useEffect, useState } from 'react';

import { FormProvider, useForm } from 'react-hook-form';

import { useGetReportingColumnsQuery, useSearchTablesMutation } from 'apis/report-configuration.api';
import { sortTablesByUpperCaseFirst } from 'constants/global';
import { useOpen } from 'hooks/useOpen';
import { DatabaseColumn } from 'interfaces/report-configuration.interface';
import { CommonError } from 'interfaces/shared.interface';
import { CreateTenantFormValues } from 'interfaces/tenants.interface';
import { ErrorModal, FilterForm, LoadingOverlay, Modal } from 'shared-components';
import ModalControlButtons from 'shared-components/ModalControlButtons';

import TableItemsList from './TableItemsList';

interface Props {
  handleClose: VoidFunction;
  isOpen: boolean;
  selectedTables: { name: string }[];
  onAddTablesConfirm: (columns: DatabaseColumn[]) => void;
}
const AddTablesModal = ({ handleClose, isOpen, selectedTables, onAddTablesConfirm }: Props) => {
  const [searchTables, { data: searchResult, isLoading: isSearchLoading, error: searchTableError }] =
    useSearchTablesMutation();
  const { data: selectedColumnsData } = useGetReportingColumnsQuery();
  const [localSelectedTables, setLocalSelectedTables] = useState<string[]>([]);
  const [isErrorModalOpen, handleCloseErrorModal] = useOpen();

  const addTablesFormMethods = useForm<CreateTenantFormValues>({ mode: 'onBlur', reValidateMode: 'onBlur' });

  const [tables, setTables] = useState<{ name: string }[]>([]);

  const { reset } = addTablesFormMethods;

  const { data: errorData } = (searchTableError as CommonError) || {};

  const selectedTablesSafe = selectedTables || [];

  const onCloseModal = () => {
    reset();
    handleClose();
  };

  const handleToggleTable = useCallback((item: { name: string }) => {
    setLocalSelectedTables((prev) => {
      if (prev.includes(item.name)) {
        return prev.filter((name) => name !== item.name);
      } else {
        return [...prev, item.name];
      }
    });
  }, []);

  const handleAddTables = useCallback(() => {
    const payload: DatabaseColumn[] = localSelectedTables.map((name) => {
      const foundColumn = selectedColumnsData?.result.find((col) => col.name === name || col.tableName === name);
      return foundColumn
        ? {
            name: foundColumn.name,
            tableName: foundColumn.tableName,
            type: foundColumn.columnType,
            displayType: '',
          }
        : {
            name,
            tableName: name,
            type: 0,
            displayType: '',
          };
    });

    onAddTablesConfirm(payload);
    handleClose();
  }, [localSelectedTables, selectedColumnsData, onAddTablesConfirm, handleClose]);

  useEffect(() => {
    if (isOpen) {
      searchTables();
      setLocalSelectedTables(selectedTablesSafe.map((item) => item.name));
    }
  }, [isOpen, searchTables, selectedTablesSafe]);

  useEffect(() => {
    if (searchResult?.result) {
      const sortedTables = sortTablesByUpperCaseFirst(searchResult.result);
      setTables(sortedTables);
    }
  }, [searchResult]);

  return (
    <>
      <ErrorModal
        errorMessage={errorData?.error?.message}
        description={errorData?.error?.details}
        isOpen={isErrorModalOpen}
        handleClose={handleCloseErrorModal}
      />
      <Modal
        dataCy="add-tables-modal"
        isOpen={isOpen}
        isDisableOutsideClick
        onClose={onCloseModal}
        heading="AddTables"
        bodyClassName="w-full"
      >
        <FormProvider {...addTablesFormMethods}>
          <form data-cy="add-tables-modal-form">
            <FilterForm
              dataCy="add-tables-search"
              placeHolder="SearchByName"
              withFilter={false}
              inputClassName="!w-full !flex-col"
            />
            <div className="max-h-[65vh] overflow-y-auto">
              <LoadingOverlay isLoading={isSearchLoading} />
              <TableItemsList
                items={tables}
                isSelected={(item) => localSelectedTables.includes(item.name)}
                onToggleColumn={handleToggleTable}
              />
            </div>
            <ModalControlButtons
              isShownSubmit
              saveButtonText="AddTables"
              closeButtonText="Cancel"
              isLoading={isSearchLoading}
              onClose={onCloseModal}
              onSubmit={handleAddTables}
            />
          </form>
        </FormProvider>
      </Modal>
    </>
  );
};

export default AddTablesModal;
